import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminReport, ReportResolution } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { relativeTime } from '../../lib/format';
import {
  householdCountLabel,
  REASON_LABELS,
  RESOLUTION_LABELS,
  resolveActionsFor,
  TARGET_KIND_LABELS,
} from '../../lib/reports';

/**
 * Report review queue (PLAN §8 UGC, §9 #3c/#13). One row per reported target;
 * reporters are only ever a count of households (D12).
 */
export function AdminReports() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const reports = useQuery({
    queryKey: ['admin', 'reports', status],
    queryFn: () => api.admin.reports.list(status),
  });
  const resolve = useMutation({
    mutationFn: (v: { id: string; resolution: ReportResolution }) =>
      api.admin.reports.resolve(v.id, { resolution: v.resolution }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'reports'] }),
  });

  return (
    <section aria-labelledby="admin-reports" className="mb-6 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="admin-reports" className="text-lg font-bold">
          Reports
        </h2>
        <div role="tablist" className="flex gap-1">
          {(['open', 'resolved'] as const).map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={status === s}
              onClick={() => setStatus(s)}
              className={`min-h-9 rounded-lg border px-3 text-sm ${
                status === s ? 'border-brand bg-brand-soft text-brand' : 'border-line'
              }`}
            >
              {s === 'open' ? 'Open' : 'Resolved'}
            </button>
          ))}
        </div>
      </div>
      {reports.isError ? <Alert tone="error">{messageForError(reports.error)}</Alert> : null}
      {resolve.isError ? <Alert tone="error">{messageForError(resolve.error)}</Alert> : null}
      {reports.isPending ? <p className="text-sm text-muted">Loading…</p> : null}
      {reports.data && reports.data.reports.length === 0 ? (
        <p className="text-sm text-muted">
          {status === 'open' ? 'Nothing to review.' : 'No resolved reports yet.'}
        </p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {reports.data?.reports.map((r) => (
          <ReportRow
            key={r.id}
            report={r}
            busy={resolve.isPending}
            onResolve={(resolution) => resolve.mutate({ id: r.id, resolution })}
          />
        ))}
      </ul>
    </section>
  );
}

function ReportRow({
  report: r,
  busy,
  onResolve,
}: {
  report: AdminReport;
  busy: boolean;
  onResolve: (resolution: ReportResolution) => void;
}) {
  return (
    <li className="flex gap-3 rounded-xl border border-line bg-surface p-3 text-sm">
      {r.target.imageUrl ? (
        <img
          src={r.target.imageUrl}
          alt=""
          className="size-16 shrink-0 rounded-lg border border-line object-cover"
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="font-medium">
          {r.target.label}{' '}
          <span className="font-normal text-muted">· {TARGET_KIND_LABELS[r.targetKind]}</span>
        </p>
        {r.target.detail || r.reportedValue ? (
          <p className="text-xs text-muted">
            {r.reportedValue ? `Reported value: ${r.reportedValue}` : ''}
            {r.reportedValue && r.target.detail ? ' · ' : ''}
            {r.target.detail ?? ''}
          </p>
        ) : null}
        <p className="text-xs text-muted">
          {householdCountLabel(r.householdCount)} ·{' '}
          {r.reasons.map((x) => `${REASON_LABELS[x.reason]} (${x.count})`).join(', ')} · last{' '}
          {relativeTime(r.lastReportedAt)}
          {r.target.hidden ? ' · currently hidden' : ''}
          {!r.target.exists ? ' · no longer exists' : ''}
        </p>
        {r.notes.length > 0 ? (
          <ul className="list-disc pl-5 text-xs text-ink">
            {r.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        ) : null}
        {r.resolution ? (
          <p className="text-xs text-muted">
            {RESOLUTION_LABELS[r.resolution]} {r.resolvedAt ? relativeTime(r.resolvedAt) : ''}
          </p>
        ) : null}
        <div className="mt-1 flex flex-wrap gap-2">
          {resolveActionsFor(r).map((a) => (
            <button
              key={a.resolution}
              type="button"
              disabled={busy}
              onClick={() => onResolve(a.resolution)}
              className={`min-h-9 rounded-lg border border-line px-2 disabled:opacity-50 ${
                a.resolution === 'removed' ? 'text-fail' : ''
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </li>
  );
}
